import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import {
  Modal,
  Button,
  Loader,
  Avatar,
  AvatarSize,
  TextField,
  Dropdown,
  Iconography,
  IconographySizes,
  IconographyThemes,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import {
  addUserSagaAction,
  updateUserSagaAction,
  getHidRolesSagaAction,
} from "../../../../../redux/saga/user/saga-actions";
import { userSliceAction } from "../../../../../redux/slices/user";
import { UserListType } from "../../../../../redux/slices/user/Types";
import {
  getUserFormDropdownMenuProps,
  USER_FORM_MODAL_BODY_CLASS,
} from "../../../../../common/utils/userFormModalDropdown";
import "./UserFormModal.scss";

type RoleDropdownOption = { label: string; value: number };

const HidUserFormModal: React.FC<{
  user?: UserListType[0];
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: "Add" | "Update";
}> = ({ user, setModalOpen, mode }) => {
  const [userDetails, setUserDetails] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    roleId: null as number | null,
  });
  const [view, setView] = React.useState<"detail" | "status">("detail");
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const [emailStatus, setEmailStatus] = React.useState<
    { status: "success" | "error" } | {}
  >({});
  const [errorMsg, setErrorMsg] = React.useState({ emailMsg: "" });
  const [selected, setSelected] = React.useState<RoleDropdownOption | null>(
    null
  );
  const userInitializedRef = React.useRef<number | null>(null);
  const { hidRoles, status } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const dropdownMenuProps = React.useMemo(() => getUserFormDropdownMenuProps(), []);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const headerText =
    view === "status"
      ? ""
      : mode === "Add"
      ? "Add new user"
      : "Update user";

  const hidRoleOptions = React.useMemo<RoleDropdownOption[]>(
    () =>
      hidRoles.map((role) => ({
        label: role.name,
        value: role.ID,
      })),
    [hidRoles]
  );

  const resolveRoleSelection = React.useCallback(
    (roleId?: number | null, roleName?: string) => {
      if (roleId != null) {
        const byId = hidRoles.find((role) => role.ID === roleId);
        if (byId) return { roleId: byId.ID, roleName: byId.name };
      }
      if (roleName) {
        const byName = hidRoles.find((role) => role.name === roleName);
        if (byName) return { roleId: byName.ID, roleName: byName.name };
      }
      return { roleId: roleId ?? null, roleName: roleName ?? "" };
    },
    [hidRoles]
  );

  React.useEffect(() => {
    dispatch(userSliceAction.resetUserFormStatus());
    return () => {
      dispatch(userSliceAction.resetUserFormStatus());
    };
  }, [dispatch]);

  React.useEffect(() => {
    document.body.classList.add(USER_FORM_MODAL_BODY_CLASS);
    return () => {
      document.body.classList.remove(USER_FORM_MODAL_BODY_CLASS);
    };
  }, []);

  React.useEffect(() => {
    dispatch(getHidRolesSagaAction());
  }, [dispatch]);

  React.useEffect(() => {
    if (mode === "Add" || !user || hidRoles.length === 0) return;
    if (formSubmitted || view !== "detail") return;
    if (userInitializedRef.current === user.ID) return;

    const { roleId, roleName } = resolveRoleSelection(user.role_id, user.role);
    if (roleId == null) return;

    userInitializedRef.current = user.ID;
    setSelected({ label: roleName, value: roleId });
    setUserDetails({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      role: roleName,
      roleId,
    });
  }, [user, hidRoles, resolveRoleSelection, mode, formSubmitted, view]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
    setUserDetails({
      ...userDetails,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const validateEmail = () => {
    if (!emailRegex.test(userDetails.email)) {
      setEmailStatus({ status: "error" });
      setErrorMsg({ emailMsg: "Enter valid email-id" });
    }
  };

  const onRoleSelect = (data: RoleDropdownOption | null) => {
    if (!data) {
      setSelected(null);
      setUserDetails({ ...userDetails, role: "", roleId: null });
      return;
    }
    const role = hidRoles.find((item) => item.ID === data.value);
    setSelected(data);
    setUserDetails({
      ...userDetails,
      role: role?.name ?? data.label,
      roleId: data.value,
    });
  };

  const isAddOrUpdateButtonEnabled =
    userDetails.firstName &&
    userDetails.lastName &&
    emailRegex.test(userDetails.email) &&
    userDetails.roleId != null;

  const addOrUpdateUser = () => {
    if (mode === "Add") {
      dispatch(
        addUserSagaAction(
          {
            first_name: userDetails.firstName,
            last_name: userDetails.lastName,
            email: userDetails.email,
            role: userDetails.role,
            role_id: userDetails.roleId ?? undefined,
            status: "Invited",
            address1: "",
            city: "",
            province: "",
            country: "",
            zip: "",
          },
          { listRefreshTarget: "hid" }
        )
      );
    } else if (user) {
      dispatch(
        updateUserSagaAction(
          {
            ID: user.ID,
            first_name: userDetails.firstName,
            last_name: userDetails.lastName,
            role_id: userDetails.roleId ?? undefined,
            status: user.status,
          },
          { listRefreshTarget: "hid" }
        )
      );
    }
  };

  const handleClose = () => {
    setView("detail");
    setFormSubmitted(false);
    dispatch(userSliceAction.resetUserFormStatus());
    setModalOpen(false);
  };

  const statusBoolean =
    mode === "Add" ? status.addUserStatus : status.updateUserStatus;
  const statusMessage =
    mode === "Add" ? status.addUserMessage : status.updateUserMessage;
  const isSubmitting = status.addUserLoading || status.updateUserLoading;

  const handleSubmit = () => {
    setFormSubmitted(true);
    setView("status");
    addOrUpdateUser();
  };

  const renderView = () => {
    if (view === "detail") {
      return (
        <>
          <form
            className="um-form__details"
            autoComplete="off"
            onSubmit={(event) => event.preventDefault()}
          >
            <Avatar size={AvatarSize.XLarge} isEditable={false} />
            <div className="um-form__row">
              <div className="um-form__field">
                <label className="um-form__label">
                  First Name<span>*</span>
                </label>
                <TextField
                  id="hid-user-first-name"
                  name="firstName"
                  autoComplete="given-name"
                  value={userDetails.firstName}
                  theme="dark"
                  onChange={handleChange}
                />
              </div>
              <div className="um-form__field">
                <label className="um-form__label">
                  Last Name<span>*</span>
                </label>
                <TextField
                  id="hid-user-last-name"
                  name="lastName"
                  autoComplete="family-name"
                  value={userDetails.lastName}
                  theme="dark"
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="um-form__field">
              <label className="um-form__label">
                E-Mail ID<span>*</span>
              </label>
              <TextField
                {...emailStatus}
                helperText={errorMsg.emailMsg}
                id="hid-user-email"
                name="email"
                autoComplete="email"
                value={userDetails.email}
                theme="dark"
                onChange={handleChange}
                onBlur={validateEmail}
                disabled={mode === "Update"}
              />
            </div>
            <div className="um-form__field">
              <label className="um-form__label">
                Role<span>*</span>
              </label>
              {status.hidRolesLoading && hidRoleOptions.length === 0 ? (
                <Loader variant={LoaderSizes.small} />
              ) : (
                <Dropdown
                  id="hid-user-role"
                  name="hid-user-role"
                  placeholder="Select Role to Assign"
                  options={hidRoleOptions}
                  theme="dark"
                  selected={selected}
                  onSelect={onRoleSelect}
                  {...dropdownMenuProps}
                />
              )}
            </div>
          </form>
          <div className="um-form__actions">
            <Button
              variant="secondary"
              label="Cancel"
              onClick={handleClose}
            />
            <Button
              variant="primary"
              label={mode === "Add" ? "Add" : "Update"}
              disabled={!isAddOrUpdateButtonEnabled}
              onClick={handleSubmit}
            />
          </div>
        </>
      );
    }

    return (
      <div className="um-form__status">
        {isSubmitting || !formSubmitted ? (
          <div className="um-form__loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : statusBoolean ? (
          <>
            <Iconography
              icon="circleCheck"
              size={IconographySizes.XLarge}
              theme={IconographyThemes.Success}
              className="um-form__status-icon"
            />
            <div className="um-form__message">
              {mode === "Add"
                ? "HID User Added with Invitation Sent"
                : "HID User Updated"}
            </div>
            <div className="um-form__user-info">
              <div className="um-form__user-name">{`${userDetails.firstName} ${userDetails.lastName}`}</div>
              <div className="um-form__user-email">{userDetails.email}</div>
              <div className="um-form__user-role">{userDetails.role}</div>
            </div>
            <Button
              variant="primary"
              label="Close"
              onClick={handleClose}
            />
          </>
        ) : (
          <>
            <Iconography
              icon="circleXmark"
              size={IconographySizes.XLarge}
              theme={IconographyThemes.Error}
              className="um-form__status-icon"
            />
            <div className="um-form__message">
              {statusMessage || "Something went wrong!"}
            </div>
            <Button
              variant="primary"
              label="Close"
              onClick={handleClose}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <Modal
      headerText={headerText}
      width={500}
      onClose={handleClose}
      className="um-user-form-modal"
    >
      <div className="um-form">{renderView()}</div>
    </Modal>
  );
};

export default HidUserFormModal;
